import React, { useState, useEffect } from 'react';
import { Modal, Button, Select, Space, Spin, message, Alert, Empty } from 'antd';
import { PrinterOutlined, DownloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { templatesApi, DocumentTemplate } from '../../services/api';
import { ExtendedContractDetails } from '../contract';

interface PrintModalProps {
  open: boolean;
  onClose: () => void;
  contract: ExtendedContractDetails;
}

const SHIPMENT_LEVEL_TYPES = [
  'BOOKING_REQUEST',
  'SHIPPING_INSTRUCTION',
  'COMMERCIAL_INVOICE',
  'PACKING_LIST',
  'BILL_OF_LADING'
];

export const PrintModal: React.FC<PrintModalProps> = ({ open, onClose, contract }) => {
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedShipmentId, setSelectedShipmentId] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const { data: typesData } = useQuery({
    queryKey: ['template-types'],
    queryFn: () => templatesApi.getTypes(),
    enabled: open,
  });

  const { data: templatesData } = useQuery({
    queryKey: ['templates', 'active'],
    queryFn: () => templatesApi.list({ isActive: true, limit: 100 }),
    enabled: open,
  });

  const templates = templatesData?.templates || [];
  const types = typesData?.types || [];

  const templatesByType = templates.reduce((acc, template) => {
    const type = template.type;
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(template);
    return acc;
  }, {} as Record<string, DocumentTemplate[]>);

  const availableTypes = types.filter(t => {
    const typeTemplates = templatesByType[t.value];
    return typeTemplates && typeTemplates.length > 0;
  });

  useEffect(() => {
    if (open && availableTypes.length > 0 && !selectedType) {
      const defaultType = availableTypes.find(t => t.value === 'CONTRACT_SUMMARY') || availableTypes[0];
      if (defaultType) {
        setSelectedType(defaultType.value);
      }
    }
  }, [open, availableTypes, selectedType]);

  useEffect(() => {
    if (selectedType) {
      const typeTemplates = templatesByType[selectedType];
      if (typeTemplates && typeTemplates.length > 0) {
        const defaultTemplate = typeTemplates.find(t => t.isDefault) || typeTemplates[0];
        if (defaultTemplate) {
          setSelectedTemplateId(defaultTemplate.id);
        }
      } else {
        setSelectedTemplateId(null);
      }
    } else {
      setSelectedTemplateId(null);
    }
  }, [selectedType, templatesByType]);

  useEffect(() => {
    if (selectedType && SHIPMENT_LEVEL_TYPES.includes(selectedType)) {
      if (contract.shipments && contract.shipments.length > 0 && !selectedShipmentId) {
        setSelectedShipmentId(contract.shipments[0]?.id || null);
      }
    }
  }, [selectedType, contract.shipments, selectedShipmentId]);

  useEffect(() => {
    let isMounted = true;

    const generatePreview = async () => {
      if (!selectedTemplateId) {
        if (isMounted) setPreviewHtml('');
        return;
      }

      if (selectedType && SHIPMENT_LEVEL_TYPES.includes(selectedType) && !selectedShipmentId) {
        if (isMounted) setPreviewHtml('');
        return;
      }

      if (isMounted) setIsPreviewLoading(true);
      try {
        const html = await templatesApi.generate(selectedTemplateId, {
          contractId: contract.id,
          shipmentId: selectedShipmentId || undefined,
          format: 'html',
        });
        if (isMounted) setPreviewHtml(html as string);
      } catch (error: any) {
        console.error('Preview generation failed:', error);
        if (isMounted) {
          setPreviewHtml(`<div style="color: red; padding: 20px; text-align: center;">
            <h3>Preview Failed</h3>
            <p>${error.response?.data?.error || error.message || 'Unknown error'}</p>
          </div>`);
        }
      } finally {
        if (isMounted) setIsPreviewLoading(false);
      }
    };

    if (open) {
      const timer = setTimeout(generatePreview, 500);
      return () => {
        clearTimeout(timer);
        isMounted = false;
      };
    }
    
    return () => {
      isMounted = false;
    };
  }, [selectedTemplateId, selectedShipmentId, contract.id, open, selectedType]);

  const handleDownload = async () => {
    if (!selectedTemplateId) return;
    
    setIsGeneratingPdf(true);
    try {
      const blob = await templatesApi.generate(selectedTemplateId, {
        contractId: contract.id,
        shipmentId: selectedShipmentId || undefined,
        format: 'pdf',
      });

      const url = window.URL.createObjectURL(blob as Blob);
      const link = document.createElement('a');
      link.href = url;
      
      const template = templates.find(t => t.id === selectedTemplateId);
      const typeLabel = types.find(t => t.value === template?.type)?.label || 'Document';
      const shipmentRef = selectedShipmentId 
        ? contract.shipments?.find(s => s.id === selectedShipmentId)?.reference 
        : null;
      
      const filename = `${contract.reference}${shipmentRef ? `-${shipmentRef}` : ''}-${typeLabel.replace(/\s+/g, '_')}.pdf`;
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      message.success('PDF downloaded successfully');
    } catch (error: any) {
      console.error('PDF generation failed:', error);
      message.error('Failed to generate PDF: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handlePrint = async () => {
    if (!selectedTemplateId) return;

    setIsGeneratingPdf(true);
    try {
      const blob = await templatesApi.generate(selectedTemplateId, {
        contractId: contract.id,
        shipmentId: selectedShipmentId || undefined,
        format: 'pdf',
      });

      const url = window.URL.createObjectURL(blob as Blob);
      
      const printWindow = window.open(url);
      if (!printWindow) {
        message.warning('Pop-up blocked. Please allow pop-ups to print.');
      }
      
    } catch (error: any) {
      console.error('Print generation failed:', error);
      message.error('Failed to prepare print: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const isShipmentLevel = selectedType ? SHIPMENT_LEVEL_TYPES.includes(selectedType) : false;
  const hasShipments = contract.shipments && contract.shipments.length > 0;
  const hasContainers = contract.shipments?.some(s => s.containers && s.containers.length > 0);

  return (
    <Modal
      title={
        <Space>
          <PrinterOutlined />
          <span>Print / Export Documents</span>
        </Space>
      }
      open={open}
      onCancel={onClose}
      width={1000}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Close
        </Button>,
        <Button 
          key="download" 
          icon={<DownloadOutlined />} 
          onClick={handleDownload}
          loading={isGeneratingPdf}
          disabled={!selectedTemplateId || (isShipmentLevel && !selectedShipmentId)}
        >
          Download PDF
        </Button>,
        <Button 
          key="print" 
          type="primary" 
          icon={<PrinterOutlined />} 
          onClick={handlePrint}
          loading={isGeneratingPdf}
          disabled={!selectedTemplateId || (isShipmentLevel && !selectedShipmentId)}
        >
          Print
        </Button>,
      ]}
      styles={{ body: { height: '70vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' } }}
    >
      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#666' }}>Document Type</label>
          <Select
            style={{ width: '100%' }}
            placeholder="Select document type"
            value={selectedType}
            onChange={setSelectedType}
            options={availableTypes.map(t => ({ label: t.label, value: t.value }))}
          />
        </div>
        
        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#666' }}>Template</label>
          <Select
            style={{ width: '100%' }}
            placeholder="Select template"
            value={selectedTemplateId}
            onChange={setSelectedTemplateId}
            disabled={!selectedType}
            options={selectedType ? templatesByType[selectedType]?.map(t => ({ label: t.name, value: t.id })) : []}
          />
        </div>

        {isShipmentLevel && (
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#666' }}>Shipment</label>
            <Select
              style={{ width: '100%' }}
              placeholder="Select shipment"
              value={selectedShipmentId}
              onChange={setSelectedShipmentId}
              disabled={!hasShipments}
              options={contract.shipments?.map(s => ({ 
                label: `Shipment ${s.shipmentNumber} (${s.reference || 'No Ref'})`, 
                value: s.id 
              }))}
            />
          </div>
        )}
      </div>

      {isShipmentLevel && !hasShipments && (
        <Alert
          message="No Shipments Available"
          description="This contract has no shipments created yet. Please create a shipment to generate this document."
          type="warning"
          showIcon
          style={{ marginBottom: '16px' }}
        />
      )}

      {isShipmentLevel && hasShipments && !hasContainers && (
        <Alert
          message="No Containers"
          description="The selected shipment has no containers. Some fields may be empty."
          type="warning"
          showIcon
          style={{ marginBottom: '16px' }}
        />
      )}

      <div style={{ 
        flex: 1, 
        border: '1px solid #f0f0f0', 
        borderRadius: '8px', 
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: '#525659'
      }}>
        {isPreviewLoading ? (
          <div style={{ 
            position: 'absolute', 
            top: 0, left: 0, right: 0, bottom: 0, 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            backgroundColor: 'rgba(255,255,255,0.8)',
            zIndex: 10
          }}>
            <Spin tip="Generating preview..." />
          </div>
        ) : previewHtml ? (
          <iframe
            srcDoc={previewHtml}
            style={{ width: '100%', height: '100%', border: 'none', backgroundColor: 'white' }}
            title="Document Preview"
          />
        ) : (
          <div style={{ 
            height: '100%', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            color: '#fff'
          }}>
            <Empty description={<span style={{ color: '#fff' }}>Select a template to preview</span>} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        )}
      </div>
    </Modal>
  );
};
